import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";

import { EventViewModel } from "../../models/eventViewModel";
import { RecordingViewModel } from "../../models/recordingViewModel";
import { formatTimestamp } from "../../../../common/utils/timestampUtils";
import StyledLoadingButton from "../../../../common/providers/theme/design-tokens/LoadingButton/StyledLoadingButton";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import CardMediaVideoPlayer from "../../../media-viewer/components/player/CardMediaVideoPlayer";
import { useState } from "react";
import { MediaFullScreen } from "../../../../common/components/media/MediaFullScreen";
import { DEFAULT_MEDIA_ICON_STYLES } from "../../../../common/variables/common";
import { getFileExtensionFromSignedUrl } from "../../../../common/components/media/fileHelper";

interface EventRecordingsTabProps {
  event: EventViewModel;
  recordings: RecordingViewModel[];
  loading: boolean;
  lastEvaluatedKey?: string | null;
  fetchNextPage: () => void;
}

const EventRecordingsTab = ({
  event,
  recordings,
  loading,
  lastEvaluatedKey,
  fetchNextPage,
}: EventRecordingsTabProps): JSX.Element => {
  const theme = useTheme();

  const [idOfPlayingVideo, setIdOfPlayingVideo] = useState<string>("");

  // Determine if this is a gas/liquid leak event (shows RATE MCF)
  const isLeakEvent = event.modelId?.toLowerCase().includes("leak");

  if (loading && recordings.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 4,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Section Header */}
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {/* Recordings List */}
        <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
          {recordings.length === 0 ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No recordings available
              </Typography>
            </Box>
          ) : (
            recordings.map(recording => {
              const isPlaying = idOfPlayingVideo === recording.id;

              return (
                <Box
                  key={recording.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    p: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    "&:last-child": {
                      borderBottom: "none",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      gap: 2,
                    }}
                  >
                    {/* Thumbnail with hover overlay */}
                    <MediaFullScreen
                      keyFrame={recording.keyFrame}
                      mediaOutput={recording.mediaOutput}
                      s3ImageFullScreenProps={{
                        sx: DEFAULT_MEDIA_ICON_STYLES,
                        fixedHeight: "60px",
                      }}
                    />

                    {/* Recording Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ minWidth: 70 }}
                        >
                          TIMESTAMP
                        </Typography>
                        <Typography variant="caption">
                          {formatTimestamp(recording.timestamp)}
                        </Typography>
                      </Box>
                      {isLeakEvent &&
                        recording.rawEventInfo?.data?.rate_mcf && (
                          <Box sx={{ display: "flex", gap: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ minWidth: 70 }}
                            >
                              RATE MCF
                            </Typography>
                            <Typography variant="caption">
                              {recording.rawEventInfo.data.rate_mcf}
                            </Typography>
                          </Box>
                        )}
                    </Box>

                    <Box>
                      <IconButton
                        onClick={async e => {
                          e.stopPropagation();

                          const downloadUrl =
                            recording.mediaOutput || recording.keyFrame;

                          if (!downloadUrl) {
                            return;
                          }

                          const fileExtension =
                            getFileExtensionFromSignedUrl(downloadUrl);

                          const response = await fetch(downloadUrl);
                          const blob = await response.blob();
                          const urlObject = URL.createObjectURL(blob);
                          const link = document.createElement("a");

                          link.href = urlObject;

                          link.setAttribute(
                            "download",
                            `${formatTimestamp(
                              recording.timestamp
                            )}${fileExtension}`
                          );

                          document.body.appendChild(link);

                          link.click();

                          URL.revokeObjectURL(urlObject);

                          link.remove();
                        }}
                      >
                        <FileDownloadOutlinedIcon fontSize="medium" />
                      </IconButton>

                      {isLeakEvent &&
                        (isPlaying ? (
                          <IconButton
                            size="medium"
                            onClick={e => {
                              e.stopPropagation();

                              setIdOfPlayingVideo("");
                            }}
                          >
                            <ExpandLessOutlinedIcon fontSize="medium" />
                          </IconButton>
                        ) : (
                          <IconButton
                            size="medium"
                            onClick={e => {
                              e.stopPropagation();

                              setIdOfPlayingVideo(recording.id);
                            }}
                          >
                            <ExpandMoreOutlinedIcon fontSize="medium" />
                          </IconButton>
                        ))}
                    </Box>
                  </Box>

                  {isLeakEvent && isPlaying && (
                    <Box
                      sx={{
                        width: "100%",
                        height: "280px",

                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 2,
                        textAlign: "center",
                        paddingTop: 2,
                      }}
                    >
                      <CardMediaVideoPlayer
                        src={recording.mediaOutput}
                        controls
                        autoPlay
                        sx={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "8px",
                          backgroundColor: "black",
                        }}
                      />
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </Box>

        {/* Load More Button */}
        {lastEvaluatedKey && (
          <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <StyledLoadingButton
              loading={loading}
              variant="outlined"
              fullWidth
              onClick={fetchNextPage}
            >
              Load more
            </StyledLoadingButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EventRecordingsTab;
